<template>
    <div>
        <b-modal id="cost-unit-detail-modal" hide-footer size="xl" title="Details Schadenposition"
            :visible="isModalOpen" @hide="reset()" @show="loadCostUnit">
            <div v-if="isLoading" class="d-flex justify-content-center pt-1">
                <csp-loading-indicator/>
            </div>
            <div v-else>
                <csp-record-detail-cost-unit-payment-deletion-modal
                    :cost-unit-id="costUnitId"
                    :is-modal-open="shouldShowDeletionModal"
                    :payment-id="deletionPaymentId"
                    :record-id="recordId"
                    @close="resetDeletionModal"
                    @update:showModal="val => shouldShowDeletionModal = val"
                    @updated="$emit('updated');$emit('close')"/>
                <b-card class="mb-4 info-card">
                    <b-row>
                        <b-col v-for="(value, key) in costUnit" :key="key" cols="4">
                            <div class="info-card-element">
                                <label>
                                    {{ labels[key] || key }}
                                </label>
                                <div class="value">
                                    {{ formatters[key] ? formatters[key](value) : value }}
                                </div>
                            </div>
                        </b-col>
                        <b-col cols="4">
                            <div class="info-card-element">
                                <label>
                                    {{ labels['hide'] || 'hide' }}
                                </label>
                                <div class="value">
                                    <csp-hide-for-group
                                        :default-value="hideForGroupValues"
                                        :options="hideForGroupOptions"
                                        :record-id="recordId"/>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </b-card>
                <div v-if="payments.length > 0">
                    <hr>
                    <h2 class="mb-4">
                        Zahlungen
                    </h2>
                </div>
                <b-card v-for="payment in payments" :key="payment.id" class="info-card">
                    <div class="float-right mb-1">
                        <b-button
                            v-if="payment.meta.permissions.canMarkAsAdvice"
                            size="sm"
                            variant="primary"
                            @click="markPaymentAsAdvice(payment.id)">
                            als Zahlungsavis markieren
                        </b-button>
                        <b-button
                            v-if="payment.meta.permissions.canDelete"
                            size="sm"
                            variant="primary"
                            @click="deletePayment(payment)">
                            Löschen
                        </b-button>
                    </div>
                    <div class="clearfix"/>
                    <b-row>
                        <b-col v-for="(value, key) in filteredPaymentAttributes(payment)" :key="key" cols="4">
                            <div class="info-card-element">
                                <label>
                                    {{ labels[key] || key }}
                                </label>
                                <div class="value">
                                    {{ formatters[key] ? formatters[key](value) : value }}
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                    <hr>
                </b-card>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import '@desktop/scss/components/record/document-upload-modal.scss';
    import {formatDate, formatDateTime} from '@js/functions/date';
    import {formatCurrencyCents} from '@js/functions/format';
    import RecordCostUnitPaymentDeletionModal
        from '@desktop/components/record/cost-unit/RecordCostUnitPaymentDeletionModal.vue';

    const container = Container.getInstance();
    const recordRepository = container.getDesktopRecordRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        name: 'CostUnitDetailModal',
        components: {'csp-record-detail-cost-unit-payment-deletion-modal': RecordCostUnitPaymentDeletionModal},
        props: {
            hideForGroupOptions: {
                type: Array,
                default: null,
            },
            costUnitId: {
                type: Number,
                default: null,
            },
            isModalOpen: {
                type: Boolean,
                default: false,
            },
            recordId: {
                type: Number,
                required: true,
            },
        },
        emits: ['close', 'updated'],
        data() {
            return {
                costUnit: {},
                payments: [],
                isLoading: true,
                hideForGroupValues: [],
                shouldShowDeletionModal: false,
                deletionPaymentId: null,
                labels: {
                    costUnitId: 'ID',
                    receipt: 'Beleg Nr.',
                    costUnitText: 'Bezeichnung',
                    source: 'Quelle',
                    insuranceFigure: 'Bezifferungsart / Vorsteuerabzug',
                    amountNet: 'Forderung (netto)',
                    amountGross: 'Forderung (brutto)',
                    vatRate: 'USt.',
                    amountVat: 'USt.-Betrag',
                    amountPaidNet: 'Eingegangen (netto)',
                    amountPaidGross: 'Eingegangen (brutto)',
                    datePaid: 'bezahlt am',
                    amountOpenNet: 'Offen (netto)',
                    amountOpenGross: 'Offen (brutto)',
                    amountAdjustmentNet: 'Vergleich (netto)',
                    amountAdjustmentGross: 'Vergleich (brutto)',
                    vatCorrectionFromAdvance: 'Korrektur USt. aus Vorschuss',
                    vatCorrectionFromAdjustment: 'Korrektur USt. aus Vergleich',
                    amountCopayment: 'Selbstbeteiligung',
                    dateReceipt: 'beziffert am',
                    paymentComment: 'Bemerkung',
                    userName: 'Hinzugefügt von',
                    dateSet: 'Erstellungsdatum',
                    dateUpdated: 'Letzte Änderung',
                    hide: 'Nicht anzeigen für',
                    costUnitPaymentId: 'ID',
                    paymentType: 'Art der Zahlung',
                    description: 'Bemerkung',
                    isPaymentAdvice: 'ist Zahlungsavis',
                    workUnitsPerHour: 'AW pro Stunde',
                    bodyWorkWorkUnits: 'AW (Karosserie)',
                    bodyWorkHourlyRate: 'Stundensatz (Karosserie)',
                    bodyWorkTotal: 'Summe (Karosserie)',
                    lacquerWorkUnits: 'AW (Lackierung)',
                    lacquerHourlyRate: 'Stundensatz (Lackierung)',
                    lacquerTotal: 'Summe (Lackierung)',
                    lacquerMaterial: 'Lackmaterial',
                    sparePartsTotal: 'Ersatzteile Summe',
                    additionalCosts: 'Nebenkosten',
                },
                formatters: {
                    dateReceipt: formatDate,
                    datePaid: formatDate,
                    dateSet: formatDateTime,
                    dateUpdated: formatDateTime,
                    amountNet: formatCurrencyCents,
                    amountGross: formatCurrencyCents,
                    amountVat: formatCurrencyCents,
                    amountAdjustmentNet: formatCurrencyCents,
                    amountAdjustmentGross: formatCurrencyCents,
                    amountPaidNet: formatCurrencyCents,
                    amountPaidGross: formatCurrencyCents,
                    amountOpenNet: formatCurrencyCents,
                    amountOpenGross: formatCurrencyCents,
                    vatCorrectionFromAdjustment: formatCurrencyCents,
                    vatCorrectionFromAdvance: formatCurrencyCents,
                    amountCopayment: formatCurrencyCents,
                    bodyWorkHourlyRate: formatCurrencyCents,
                    bodyWorkTotal: formatCurrencyCents,
                    lacquerHourlyRate: formatCurrencyCents,
                    lacquerTotal: formatCurrencyCents,
                    lacquerMaterial: formatCurrencyCents,
                    sparePartsTotal: formatCurrencyCents,
                    additionalCosts: formatCurrencyCents,
                    vatRate: (value) => `${value}%`,
                },
            };
        },
        methods: {
            filteredPaymentAttributes(payment) {
                const attributes = {...payment.attributes};

                if (attributes.isPaymentAdvice === null) {
                    delete attributes.isPaymentAdvice;
                }
                if (attributes.source === null) {
                    delete attributes.source;
                }
                // never displayed, just used for logic
                delete attributes.isSapPayment;

                return attributes;
            },
            deletePayment(payment) {
                if (payment.attributes.isSapPayment) {
                    // show modal with all payments belonging to same sap payment as deleted payment
                    // to confirm deletion of all of them
                    this.deletionPaymentId = Number(payment.id);
                    this.shouldShowDeletionModal = true;
                    return;
                }
                this.isLoading = true;
                recordRepository.deleteCostUnitPayment(this.recordId, this.costUnitId, payment.id)
                    .then(() => {
                        flashMessenger.addSuccess('Zahlung erfolgreich gelöscht.');
                        this.$emit('updated');
                        this.reset();
                    })
                    .catch(() => {
                        this.isLoading = false;
                    });
            },
            loadCostUnit() {
                this.isLoading = true;
                recordRepository.loadCostUnitDetail(this.recordId, this.costUnitId).then(
                    (costUnit) => {
                        this.costUnit = costUnit.data.attributes;
                        this.payments = costUnit.data.attributes.payments.data;
                        this.hideForGroupValues = costUnit.data.attributes.hide;
                        // Remove payments from costUnit so that they are not displayed with base data
                        delete this.costUnit.hide;
                        delete this.costUnit.payments;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            },
            markPaymentAsAdvice(paymentId) {
                this.isLoading = true;
                recordRepository.markPaymentAsAdvice(this.recordId, this.costUnitId, paymentId)
                    .then(() => {
                        flashMessenger.addSuccess('Zahlung wurde als Zahlungsavis gekennzeichnet.');
                        this.$emit('updated');
                        this.reset();
                    })
                    .catch(() => {
                        this.isLoading = false;
                    });
            },
            reset() {
                this.costUnit = null;
                this.isLoading = true;

                this.$emit('close');
            },
            resetDeletionModal() {
                this.deletionPaymentId = null;
                this.shouldShowDeletionModal = false;
            },
        },
    }
</script>
